import { AsyncPipe, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  CompanyDetailsEntity,
  CompanyEntity,
  CompanyLocationEntity,
  CustomerPersonEntity,
  PersonEntity,
  ScreeningCaseCompositeEntity,
  ScreeningCaseReviewStatusEntity,
  ScreeningCaseReviewStatusKey,
} from '@nk/data-entities-lib';
import { CaretExpanderComponent, ErrorAlertComponent, ProgressComponent } from '@nk/ui-lib';
import { arrayUnique, DateFormats, LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { ScreeningCaseService } from '../../services/screening-case.service';
import { normalizeScreeningContext } from '../../utils/screening-case-review-context.utils';
import { getLatestScreeningCaseReviewCompositeEntity } from '../../utils/screening-case.utils';
import { getScreeningStatusTags, ScreeningStatusTag } from '../../utils/screening-overview.utils';
import { ScreeningAdverseMediaCardListComponent } from '../screening-adverse-media-card-list/screening-adverse-media-card-list.component';
import { ScreeningCaseCardStatusListItemComponent } from '../screening-case-card-status-list-item/screening-case-card-status-list-item.component';
import { ScreeningCaseComparisonComponent } from '../screening-case-comparison/screening-case-comparison.component';
import { ScreeningCaseReviewProgressComponent } from '../screening-case-review-progress/screening-case-review-progress.component';

interface ScreeningCaseItem {
  uuid: string;
  title: string;
  matchScore: number | null;
  screeningStatusTags: ScreeningStatusTag[];
  data: ScreeningCaseCompositeEntity;
  person: PersonEntity | null;
  customerPerson: CustomerPersonEntity | null;
  company: CompanyEntity | null;
  companyDetails: CompanyDetailsEntity | null;
  companyLocations: CompanyLocationEntity[];
  latestReviewComment: string | null;
  latestReviewStatusKey: ScreeningCaseReviewStatusKey;
}

const DEFAULT_PAGE_LIMIT = 100;

const REVIEW_STATUS_ICONS: Record<ScreeningCaseReviewStatusKey, string> = {
  [ScreeningCaseReviewStatusKey.pending]: '',
  [ScreeningCaseReviewStatusKey.ongoing]: '',
  [ScreeningCaseReviewStatusKey.complete]: 'fa-check-circle',
};

const REVIEW_STATUS_COLORS: Record<ScreeningCaseReviewStatusKey, string> = {
  [ScreeningCaseReviewStatusKey.pending]: '',
  [ScreeningCaseReviewStatusKey.ongoing]: '',
  [ScreeningCaseReviewStatusKey.complete]: 'text-white',
};

function transformToScreeningCaseItems(entities: ScreeningCaseCompositeEntity[]): ScreeningCaseItem[] {
  return entities
    .map((screeningCaseComposite) => {
      const screeningStatusTags = screeningCaseComposite?.screening_case_state
        ? getScreeningStatusTags(screeningCaseComposite.screening_case_state)
        : [];
      const latestReview = getLatestScreeningCaseReviewCompositeEntity(
        screeningCaseComposite.screening_case_review_composites,
      );
      const defaultTitle =
        screeningCaseComposite.person?.full_name ||
        screeningCaseComposite.company?.name ||
        screeningCaseComposite.customer_person?.full_name ||
        '-';

      const latestReviewStatusKey =
        (latestReview?.screening_case_review_status
          ?.screening_case_review_status_key as ScreeningCaseReviewStatusKey) ?? ScreeningCaseReviewStatusKey.pending;

      const supplierData = normalizeScreeningContext(screeningCaseComposite.screening_case.screening_context);
      const item: ScreeningCaseItem = {
        uuid: screeningCaseComposite.screening_case.uuid,
        title: supplierData[0]?.match_name || defaultTitle,
        matchScore: supplierData[0]?.match_score ?? null,
        screeningStatusTags,
        data: screeningCaseComposite,
        person: screeningCaseComposite.person ?? null,
        customerPerson: screeningCaseComposite.customer_person ?? null,
        company: screeningCaseComposite.company ?? null,
        companyDetails: null,
        companyLocations: [],
        latestReviewComment: latestReview?.screening_case_review?.screening_case_review_comment ?? null,
        latestReviewStatusKey,
      };
      return item;
    })
    .sort((a, b) => {
      // TODO: we should sort in the api instead
      return (
        (b.data.screening_case.created_timestamp?.valueOf() || 0) -
          (a.data.screening_case.created_timestamp?.valueOf() || 0) || 0
      );
    });
}

@Component({
  selector: 'nk-screening-entity-screening-cases',
  imports: [
    AsyncPipe,
    DatePipe,
    TranslateModule,
    AccordionModule,
    TooltipModule,
    ProgressComponent,
    ErrorAlertComponent,
    CaretExpanderComponent,
    ScreeningAdverseMediaCardListComponent,
    ScreeningCaseCardStatusListItemComponent,
    ScreeningCaseComparisonComponent,
    ScreeningCaseReviewProgressComponent,
  ],
  templateUrl: './screening-entity-screening-cases.component.html',
  styleUrl: './screening-entity-screening-cases.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningEntityScreeningCasesComponent implements OnInit {
  public screeningConfigUuid = input.required<string>();
  public screeningEntityUuid = input.required<string>();
  public printView = input<boolean>(false);

  private readonly _screeningCaseService = inject(ScreeningCaseService);
  private readonly _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private readonly _triggerLoad = new BehaviorSubject<null>(null);

  public items$!: Observable<ScreeningCaseItem[]>;
  public screeningCaseReviewStatusList$!: Observable<ScreeningCaseReviewStatusEntity[]>;
  public isLoading = signal(false);
  public error = signal<HttpErrorResponse | null>(null);
  public openPanels = signal<{ [uuid: string]: boolean }>({});
  public dateFormats = DateFormats;
  public reviewStatusIcons = REVIEW_STATUS_ICONS;
  public reviewStatusColors = REVIEW_STATUS_COLORS;
  public reviewStatusComplete = ScreeningCaseReviewStatusKey.complete;

  public ngOnInit(): void {
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this.constructor.name, true);
    }
    const screeningConfigUuid = this.screeningConfigUuid();
    const screeningEntityUuid = this.screeningEntityUuid();

    this.screeningCaseReviewStatusList$ = this._screeningCaseService.screeningCaseReviewStatusListCache.get();

    this.items$ = this._triggerLoad.pipe(
      tap(() => this.isLoading.set(true)),
      switchMap(() =>
        this._screeningCaseService.screeningEntityCaseCompositeCollectionCache.get(
          screeningConfigUuid,
          screeningEntityUuid,
          DEFAULT_PAGE_LIMIT,
          0,
        ),
      ),
      map((collection) => {
        return transformToScreeningCaseItems(collection.elements);
      }),
      switchMap((items) => {
        // Load company details and locations for items that have a company
        const companyUuids = items.filter((item) => item.company?.uuid).map((item) => item.company?.uuid as string);

        if (companyUuids.length === 0) {
          return of(items);
        }

        const uniqueCompanyUuids = arrayUnique(companyUuids);
        const companyDataRequests = uniqueCompanyUuids.map((companyUuid) =>
          forkJoin({
            companyUuid: of(companyUuid),
            details: this._screeningCaseService.companyDetailsCache.get(companyUuid).pipe(catchError(() => of([]))),
            locations: this._screeningCaseService.companyLocationCache.get(companyUuid).pipe(catchError(() => of([]))),
          }),
        );

        return forkJoin(companyDataRequests).pipe(
          map((companyDataList) => {
            const companyDataMap = new Map(
              companyDataList.map((data) => [
                data.companyUuid,
                { details: data.details[0] ?? null, locations: data.locations },
              ]),
            );

            return items.map((item) => {
              if (item.company?.uuid) {
                const companyData = companyDataMap.get(item.company.uuid);
                if (companyData) {
                  return {
                    ...item,
                    companyDetails: companyData.details,
                    companyLocations: companyData.locations,
                  };
                }
              }
              return item;
            });
          }),
        );
      }),
      tap((items) => {
        // Initialize panels: all open in print view, otherwise only not-completed cases
        const openPanels: { [uuid: string]: boolean } = {};
        for (const item of items) {
          openPanels[item.uuid] =
            this.printView() || item.latestReviewStatusKey !== ScreeningCaseReviewStatusKey.complete;
        }
        this.openPanels.set(openPanels);
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this.constructor.name, false);
        }
        this.isLoading.set(false);
      }),
      catchError((err: HttpErrorResponse) => {
        this.error.set(err);
        this.isLoading.set(false);
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this.constructor.name, false);
        }
        return of([]);
      }),
    );
  }

  public onPanelToggle(uuid: string, isOpen: boolean): void {
    const openPanels = this.openPanels();
    openPanels[uuid] = isOpen;
    this.openPanels.set(openPanels);
  }

  public onReviewSaved(): void {
    this._screeningCaseService.screeningEntityCaseCompositeCollectionCache.invalidateAllCache();
    this._triggerLoad.next(null);
  }

  public onReviewCreated(): void {
    this._screeningCaseService.screeningEntityCaseCompositeCollectionCache.invalidateAllCache();
    this._triggerLoad.next(null);
  }
}
